import axios from "axios";

// Общий слой запроса форматов к /api/convert для таблицы одиночного видео и карточки плейлиста (MainResults.vue):
// один запрос, один разбор ответа и одна раскладка ошибок для GA — правка контракта API делается здесь.

export const NOT_FOUND_MESSAGE = 'The download link was not found';
export const CONVERT_TIMEOUT = 45000;

// Кэш успешных ответов по sf_url на 10 минут (как IgApi.cache): подпись (ts, _s) в ключ не входит.
const CONVERT_CACHE_TTL = 10 * 60 * 1000;
const convertCache = {};

// Копия data: MainResults меняет элементы ответа (url, inline), кэш должен остаться нетронутым.
function fromCache(entry) {
    return { ...entry.response, data: JSON.parse(JSON.stringify(entry.response.data)) };
}

// body — подписанное тело запроса ($root.subscribeSignedRequestBody)
export async function postConvert(body) {
    const key = body.sf_url;
    const cached = convertCache[key];
    if (cached && Date.now() - cached.time < CONVERT_CACHE_TTL) return fromCache(cached);

    const response = await axios.post('/api/convert', new URLSearchParams(body), { timeout: CONVERT_TIMEOUT });
    const data = response.data;
    if (key && response.status === 200 && data && data.success !== false && !data.blacklist) {
        convertCache[key] = { time: Date.now(), response };
        return fromCache(convertCache[key]);
    }
    return response;
}

// Ответ на ссылку одного видео → { formats } или { error, blacklist }.
// error — payload для GA (show-error): blacklist и ответы с признаком, который ga.js разбирает сам
// (success: false, supported_resource: null), — как есть; остальное (нет url, пустой список) — link_not_found.
// Например 200 { response: 4, response_type: 'link not found', success: false } — ошибка, а не пустой результат.
export function parseVideoResponse(data) {
    const obj = data && typeof data === 'object' && !Array.isArray(data) ? data : null;
    if (obj && obj.blacklist) return { error: obj, blacklist: true };
    const formats = obj && Array.isArray(obj.url) ? prepareFormats(obj.url) : [];
    if (formats.length) return { formats };
    const known = obj && (obj.success === false || obj.supported_resource === null);
    return { error: known ? obj : { success: false }, blacklist: false };
}

// Ошибка axios (catch) → { ga, message, status }.
// e.response нет на таймауте и при сбое подписи — поля undefined не читаются.
// ga: таймаут → { timeout: true } (timeout_exeeded); ответ со status >= 400 → сам ответ
// (link_not_found / response-error для 401); без ответа → undefined (fatal).
// message — текст для пользователя: у 401 — текст сервера, иначе NOT_FOUND_MESSAGE.
export function parseConvertError(e) {
    const r = e && e.response;
    const status = r && r.status >= 400 ? r.status : null;
    const ga = e && e.code === 'ECONNABORTED' ? { timeout: true } : status ? r : r && r.data;
    const message = (status === 401 && r.data && r.data.error_message) || NOT_FOUND_MESSAGE;
    return { ga, message, status };
}

// Порядок форматов внутри группы: MP4, затем WEBM, остальные — по алфавиту.
const EXT_ORDER = ['mp4', 'webm'];
function extRank(item) {
    const i = EXT_ORDER.indexOf(String(item.ext || '').toLowerCase());
    return i === -1 ? EXT_ORDER.length : i;
}

// Сначала со звуком, затем без звука; внутри — по формату (MP4 1080…360, затем WEBM 1080…360 и т. д.),
// в формате — по убыванию качества. Компаратор согласованный (равные → 0), порядок не зависит от движка.
function compareFormats(a, b) {
    if (!a.no_audio !== !b.no_audio) return a.no_audio ? 1 : -1;
    const ar = extRank(a);
    const br = extRank(b);
    if (ar !== br) return ar - br;
    if (ar === EXT_ORDER.length) {
        const byExt = String(a.ext || '').localeCompare(String(b.ext || ''));
        if (byExt) return byExt;
    }
    return (parseInt(b.quality) || 0) - (parseInt(a.quality) || 0);
}

// Форматы для таблицы одиночного видео и списка качеств карточки плейлиста: без дублей, сначала видео,
// затем аудио-дорожки (M4A, OPUS и т. п.); каждая часть отсортирована compareFormats.
// Первым остаётся лучшее видео со звуком — его выбирает карточка плейлиста по умолчанию.
export function prepareFormats(urls) {
    const videos = removeDuplicates(urls.filter((item) => item.audio !== true)).sort(compareFormats);
    const audios = removeDuplicates(urls.filter((item) => item.audio === true)).sort(compareFormats);
    return videos.concat(audios);
}

function removeDuplicates(videos) {
    const unique = new Set();
    return videos.filter((item) => {
        const key = item.quality + item.ext + item.no_audio;
        const duplicate = unique.has(key);
        unique.add(key);
        return !duplicate;
    });
}
